import { Box, Button, Flex, Text } from '@chakra-ui/react';
import { useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Modal } from '@morressier/alembic';
import { faFile } from '@fortawesome/pro-regular-svg-icons';
import { faBoxArchive } from '@fortawesome/pro-light-svg-icons';
import { IGetSubmissionWithdrawalResponse } from '@publishing-engine/submission-service/src/endpoints/api.response-types';
import { IRoleType } from '@publishing-engine/domain/models';
import FileViewerButtonText from '../FileViewerButtonText';

type IWithdrawalType =
  | 'EditorialSubmissionWithdrawn'
  | 'AuthorSubmissionWithdrawalRequested';

interface Props {
  type: IWithdrawalType;
  data: IGetSubmissionWithdrawalResponse;
  onClose: () => void;
  role: IRoleType;
  submissionContextId?: string;
}

export const WithdrawalContextModal = ({
  onClose,
  data,
  type,
  role,
  submissionContextId
}: Props) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      scrollRef.current?.scrollTo({ top: 0 });
    });
    return () => cancelAnimationFrame(raf);
  }, []);

  const {
    requestedByAuthor,
    withdrawalReason,
    authorResponse,
    attachments,
    editorComment
  } = data || {};

  const showWithdrawalReason =
    role === 'editorial' ||
    type === 'AuthorSubmissionWithdrawalRequested' ||
    type === 'EditorialSubmissionWithdrawn';

  return (
    <Modal
      isOpen
      closeButtonProps={{
        hidden: true
      }}
      onClose={onClose}
      width="720px"
    >
      <Flex
        ref={scrollRef}
        padding={8}
        gap={6}
        flexDirection="column"
        maxHeight="80vh"
        overflow="auto"
      >
        <Box>
          <FontAwesomeIcon fontSize={32} icon={faBoxArchive} />
        </Box>

        <Flex flexDirection="column" gap={2}>
          <Text textStyle="headingGroup">
            Withdrawal{' '}
            {type === 'AuthorSubmissionWithdrawalRequested'
              ? 'Request'
              : 'Context'}
          </Text>
          {type === 'EditorialSubmissionWithdrawn' &&
            (requestedByAuthor ? (
              <Text textStyle="textBody">
                This Withdrawal was requested by the Submission’s Author.
              </Text>
            ) : (
              <Text textStyle="textBody">
                The Editorial team processed this Withdrawal without a request
                from the Authors of the Submission.
              </Text>
            ))}
        </Flex>

        {showWithdrawalReason ? (
          <Flex gap={2} flexDirection="column">
            <Text textStyle="headingItem">Withdrawal Reason</Text>
            <Text textStyle="textBody">{withdrawalReason}</Text>
          </Flex>
        ) : null}

        {authorResponse && (
          <Flex gap={2} flexDirection="column">
            <Flex alignItems="center" gap={2}>
              <Text textStyle="headingItem">Response to Editor</Text>
            </Flex>
            <Text textStyle="textBody">{authorResponse}</Text>
          </Flex>
        )}
        {editorComment && (
          <Flex gap={2} flexDirection="column">
            <Flex alignItems="center" gap={2}>
              <Text textStyle="headingItem">Response to Author</Text>
            </Flex>
            <Text textStyle="textBody">{editorComment}</Text>
          </Flex>
        )}

        {!!attachments?.length && (
          <Flex gap={2} flexDirection="column">
            <Text textStyle="headingItem">Attached Files</Text>
            <Flex flexDirection="column" marginTop={2} gap={2}>
              {attachments.map(attachment => (
                <Flex
                  justifyContent="space-between"
                  key={attachment.referenceName}
                >
                  <Flex gap={2} alignItems="center">
                    <FontAwesomeIcon icon={faFile} />
                    <Text textStyle="textBody">{attachment.referenceName}</Text>
                    <FileViewerButtonText
                      file={attachment}
                      submissionContextId={submissionContextId}
                      textStyle="textBody"
                      flexShrink={0}
                    >
                      View
                    </FileViewerButtonText>
                  </Flex>
                </Flex>
              ))}
            </Flex>
          </Flex>
        )}

        <Flex
          gap={2}
          justifyContent="flex-end"
          alignItems="center"
          alignSelf="stretch"
        >
          <Button onClick={onClose} variant="primary" borderRadius="small">
            Close
          </Button>
        </Flex>
      </Flex>
    </Modal>
  );
};
